/**
 * ProviderSLAChart — 7-day rolling latency + uptime for Mansa + FMP.
 *
 * Sits on the Admin › Symbol Registry page. Auto-refreshes every 60 s
 * so ops can watch a live degradation event unfold.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { RefreshCw, TrendingDown, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Jargon } from "@/components/admin/JargonTerm";

function fmtTs(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return `${d.getMonth() + 1}/${d.getDate()} ${d.getHours()}:00`;
}

function SLAKpiCard({ label, value, sub, tone }) {
    const toneClasses = {
        good:  "text-emerald-500",
        warn:  "text-amber-500",
        bad:   "text-rose-500",
        muted: "text-muted-foreground",
    }[tone || "muted"];
    return (
        <div className="border border-border rounded-lg p-3">
            <div className="text-xs text-muted-foreground">{label}</div>
            <div className={`text-2xl font-bold font-mono ${toneClasses}`}>{value}</div>
            {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
        </div>
    );
}

function ProviderPane({ name, prettyName, data }) {
    if (!data) return null;
    const okPct = data.sla_7d?.ok_pct;
    const avgL  = data.sla_7d?.avg_latency_ms;
    const p95L  = data.sla_7d?.p95_latency_ms;
    const samples = data.sla_7d?.samples || 0;

    const okTone   = okPct === null ? "muted" : okPct >= 99 ? "good" : okPct >= 95 ? "warn" : "bad";
    const latTone  = avgL === null ? "muted" : avgL <= 300 ? "good" : avgL <= 800 ? "warn" : "bad";
    const chartRows = (data.series || []).map((r) => ({
        ts:      fmtTs(r.ts),
        latency: r.avg_latency_ms,
        ok:      r.ok_pct,
    }));

    return (
        <div className="border border-border rounded-lg p-4 space-y-3" data-testid={`sla-pane-${name}`}>
            <div className="flex items-center justify-between">
                <div className="font-semibold flex items-center gap-2">
                    {prettyName}
                    {okPct !== null && okPct >= 99
                        ? <Badge className="bg-emerald-500/20 text-emerald-400"><CheckCircle2 className="w-3 h-3 mr-1" /> Healthy</Badge>
                        : okPct !== null && okPct < 95
                            ? <Badge className="bg-rose-500/20 text-rose-400"><TrendingDown className="w-3 h-3 mr-1" /> Degraded</Badge>
                            : <Badge variant="outline">Watching</Badge>}
                </div>
                <div className="text-[11px] text-muted-foreground">{samples.toLocaleString()} samples · {data.series?.length || 0} hourly buckets</div>
            </div>

            <div className="grid grid-cols-3 gap-2">
                <SLAKpiCard
                    label={<>7-day <Jargon term="uptime" /></>}
                    value={okPct !== null && okPct !== undefined ? `${okPct.toFixed(2)}%` : "—"}
                    sub="ok / total probes"
                    tone={okTone}
                />
                <SLAKpiCard
                    label={<>Avg <Jargon term="latency" /></>}
                    value={avgL !== null && avgL !== undefined ? `${avgL} ms` : "—"}
                    sub="rolling 7-day"
                    tone={latTone}
                />
                <SLAKpiCard
                    label={<Jargon term="p95_latency" />}
                    value={p95L !== null && p95L !== undefined ? `${p95L} ms` : "—"}
                    sub="95th percentile"
                    tone={latTone}
                />
            </div>

            {chartRows.length === 0 ? (
                <div className="text-xs text-muted-foreground p-4 text-center border border-dashed border-border rounded">
                    Collecting samples… the probe runs every 5 minutes. Come back in a few minutes.
                </div>
            ) : (
                <div className="h-40" data-testid={`sla-chart-${name}`}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartRows} margin={{ top: 4, right: 8, bottom: 4, left: -18 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                            <XAxis dataKey="ts" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                            <YAxis
                                yAxisId="lat"
                                tick={{ fontSize: 10 }}
                                domain={[0, "auto"]}
                                label={{ value: "ms", position: "insideTopLeft", fontSize: 9, offset: -2 }}
                            />
                            <Tooltip
                                contentStyle={{
                                    background: "hsl(var(--card))",
                                    border: "1px solid hsl(var(--border))",
                                    borderRadius: 6,
                                    fontSize: 11,
                                }}
                                formatter={(v, k) => (k === "latency" ? [`${v} ms`, "Avg latency"] : [`${v}%`, "OK %"])}
                            />
                            <ReferenceLine yAxisId="lat" y={800} stroke="var(--amber-500, #f59e0b)" strokeDasharray="3 3" label={{ value: "SLA 800ms", fontSize: 9, position: "right" }} />
                            <Line
                                yAxisId="lat"
                                type="monotone"
                                dataKey="latency"
                                stroke="#38bdf8"
                                strokeWidth={2}
                                dot={{ r: 2 }}
                                isAnimationActive={false}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
}

export default function ProviderSLAChart() {
    const [sla, setSla]         = useState(null);
    const [loading, setLoading] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/symbols/sla?days=7");
            setSla(data);
        } catch {
            /* silent — endpoint requires admin auth */
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        load();
        const id = setInterval(load, 60_000);
        return () => clearInterval(id);
    }, []);

    return (
        <Card data-testid="provider-sla-card">
            <CardHeader className="flex-row items-center justify-between space-y-0">
                <CardTitle className="text-lg">Provider <Jargon term="sla" /> · 7-day</CardTitle>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={load}
                    disabled={loading}
                    data-testid="provider-sla-refresh"
                >
                    <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
                    Refresh
                </Button>
            </CardHeader>
            <CardContent className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <ProviderPane name="mansa" prettyName="Mansa (African feeds)" data={sla?.mansa} />
                <ProviderPane name="fmp"   prettyName="FMP (Global feeds)"    data={sla?.fmp}   />
            </CardContent>
        </Card>
    );
}
